import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';

import {supplierDetailsStore} from '@store/suppliers/details/supplier-details.store';
import {SupplierDetails} from '@store/suppliers/details/supplier-details.model';
import {getSupplierDetailsDataSource} from '@store/suppliers/details/supplier-details.requests';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';
import {ManageContact, ManageOrganisation} from '@store/common/organisation.model';
import {GlobalStatus} from '@store/common/globalStatus.enum';

export class SupplierDetailsService {
  store = supplierDetailsStore;

  resetStore = () => this.store.reset();

  getSupplierById = (supplierId: number | string): Observable<SupplierDetails> => {
    return from(APIAxios(APIRoutes.GETSupplierById(supplierId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SupplierDetails>) => {
          return response.data;
        }),
        tap((supplier) =>
          this.store.update((state) => ({
              ...state,
              ...supplier,
            }),
            getSupplierDetailsDataSource.setSuccess(),
          )
        ),
        getSupplierDetailsDataSource.trackRequestStatus(),
      );
  };

    getContacts = (supplierId: number | string) => {
        return from(APIAxios(APIRoutes.GETSupplierContact(supplierId)))
            .pipe(
                catchError((err: AxiosError) => {
                    throw new SnackError((err.response as any)?.data?.message, 'error');
                })
            );
    };

  updateSupplierStatus = (supplierId: number | string, status: GlobalStatus): Observable<AxiosResponse> => {
    return from(APIAxios({
      ...APIRoutes.PATCHSupplierStatus(supplierId),
      data: {status},
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        tap(() => {
          this.store.update((state) => ({
            ...state,
            status,
          }));
        }),
      );
  };

  updateSupplier = (supplierId: number | string, data: ManageOrganisation): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTSupplierById(supplierId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ManageOrganisation>) => {
          return response.data;
        }),
        switchMap(() => this.getSupplierById(supplierId)),
      );
  };

  deleteSupplier = (supplierId: number | string): Observable<AxiosResponse> => {
    return from(APIAxios(APIRoutes.DELETESupplier(supplierId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
      );
  };

  createSupplierContact = (supplierId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.POSTSupplierContact(supplierId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierById(supplierId)),
      );
  };

  updateSupplierContact = (supplierId: number | string, contactId: number | string, data: ManageContact): Observable<ManageOrganisation> => {
    return from(APIAxios({
      ...APIRoutes.PUTSupplierContactById(supplierId, contactId),
      data,
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierById(supplierId)),
      );
  };

  deleteSupplierContact = (supplierId: number | string, contactId: number | string): Observable<ManageOrganisation> => {
    return from(APIAxios(APIRoutes.DELETESupplierContact(supplierId, contactId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        switchMap(() => this.getSupplierById(supplierId)),
      );
  };
}

export const supplierDetailsService = new SupplierDetailsService();
